import React, { useState, useCallback, useMemo } from 'react';
import { uploadFileComplete, analyzeFiles, getUserFiles, updateFileTags, getUserTags, createTag, addTagToFiles, updateVirtualPath } from '../../services/api';
import UploadReviewModal from './UploadReviewModal';

const S3FileUpload = ({ onUploadSuccess, onUploadError, onFilesSelected, className = "" }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [perFileStatus, setPerFileStatus] = useState({}); // { name: { progress, status, error, success, uploadId } }
  const [showReview, setShowReview] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingNames, setExistingNames] = useState([]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFiles(files);
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFiles(files);
    }
  }, []);

  const handleFiles = async (files) => {
    // Delegate to parent to use the unified Upload Options modal (Photos-style)
    if (typeof onFilesSelected === 'function') {
      onFilesSelected(files);
      return;
    }
    setPendingFiles(files);
    // fetch existing filenames for duplicate detection
    try {
      const existing = await getUserFiles();
      setExistingNames(existing.map((f) => f.filename).filter(Boolean));
    } catch (_) {
      setExistingNames([]);
    }
    setShowReview(true);
  };

  const startUploads = async (filesToUpload, options = {}) => {
    setShowReview(false);
    setIsUploading(true);
    setUploadStatus('Uploading files...');

    // initialize per-file status
    const initial = {};
    filesToUpload.forEach((f) => {
      initial[f.name] = { progress: 0, status: 'Queued', error: null, success: false, uploadId: null };
    });
    setPerFileStatus(initial);

    // normalize options
    const selectedTagNames = (options?.tagIds || []).map((t) => String(t || '').trim()).filter(Boolean);
    const templatePath = String(options?.templatePath || '').trim();

    const ensureTagsExistAndGetIds = async (tagNames) => {
      if (!tagNames || tagNames.length === 0) return [];
      // Load existing tags and build a map by lowercase name
      let existing = [];
      try { existing = await getUserTags(); } catch (_) { existing = []; }
      const nameToId = new Map((existing || []).map((t) => [String(t.name || '').trim().toLowerCase(), t.tag_id]));
      const ids = [];
      for (const name of tagNames) {
        const key = name.toLowerCase();
        let id = nameToId.get(key);
        if (!id) {
          try {
            const res = await createTag({ name });
            id = res?.tag_id;
            if (id) nameToId.set(key, id);
          } catch (e) {
            // Non-fatal; skip creating this tag entity
          }
        }
        if (id) ids.push(id);
      }
      return ids;
    };

    const uploadSingle = async (file) => {
      const update = (patch) => setPerFileStatus((prev) => ({ ...prev, [file.name]: { ...(prev[file.name] || {}), ...patch } }));
      try {
        update({ status: 'Getting upload URL...', progress: 10 });
        const result = await uploadFileComplete(file, (progress, status) => {
          update({ progress, status });
        });
        update({ status: 'Analyzing...', progress: 85, uploadId: result.upload_id });
        try {
          await analyzeFiles([result.upload_id]);
        } catch (e) {
          // log but continue
          update({ status: 'Uploaded (analysis failed)', success: true, error: null, progress: 100 });
          return result;
        }
        // Apply user-selected tags to this file
        try {
          if (selectedTagNames.length > 0) {
            // 1) Persist human-readable tags for AI context
            await updateFileTags(result.upload_id, selectedTagNames);
            // 2) Ensure tag entities exist and apply them so Tag Manager can see them
            const tagIds = await ensureTagsExistAndGetIds(selectedTagNames);
            if (tagIds.length > 0) {
              for (const tagId of tagIds) {
                try { await addTagToFiles(tagId, [result.upload_id]); } catch (_) {}
              }
            }
          }
          // Optional: apply virtual path if provided
          if (templatePath) {
            try { await updateVirtualPath(result.upload_id, templatePath); } catch (_) {}
          }
        } catch (_) {}

        update({ status: 'Completed', success: true, error: null, progress: 100 });
        return result;
      } catch (err) {
        update({ status: 'Failed', error: err.message, success: false });
        throw err;
      }
    };

    const results = [];
    const errors = [];
    for (const file of filesToUpload) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await uploadSingle(file);
        results.push(res);
      } catch (e) {
        errors.push({ file: file.name, error: e.message });
      }
    }

    setIsUploading(false);
    if (results.length && onUploadSuccess) onUploadSuccess(results);
    if (errors.length && onUploadError) onUploadError(new Error(`${errors.length} file(s) failed`));
    setUploadStatus(errors.length ? `${errors.length} failed, ${results.length} succeeded` : 'All files uploaded');
  };

  const retryFile = async (name) => {
    const file = pendingFiles.find((f) => f.name === name);
    if (!file) return;
    setIsUploading(true);
    try {
      await startUploads([file]);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-8 transition-all duration-200
          ${isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isUploading && document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.csv,.xls,.xlsx"
        />
        
        <div className="text-center">
          {isUploading ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-lg font-medium text-gray-700">{uploadStatus || 'Uploading...'}</span>
              </div>

              <div className="space-y-2">
                {Object.entries(perFileStatus).map(([name, s]) => (
                  <div key={name} className="p-2 border border-gray-200 rounded">
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate max-w-[60%]" title={name}>{name}</span>
                      <span className={`ml-2 ${s.success ? 'text-green-600' : s.error ? 'text-red-600' : 'text-gray-600'}`}>{s.status}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className={`h-2 rounded-full transition-all duration-300 ${s.error ? 'bg-red-500' : s.success ? 'bg-green-600' : 'bg-blue-600'}`} style={{ width: `${s.progress || 0}%` }}></div>
                    </div>
                    {s.error && (
                      <div className="flex items-center justify-between mt-2 text-xs text-red-600">
                        <span>{s.error}</span>
                        <button onClick={() => retryFile(name)} className="text-blue-600 hover:underline">Retry</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900">Upload Files to S3</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Drag and drop your files here, or click to select files
                </p>
              </div>
              
              <div className="text-xs text-gray-500">
                <p>Supported formats: PDF, DOC, DOCX, PNG, JPG, JPEG, CSV, XLS, XLSX</p>
                
              </div>
            </div>
          )}
        </div>
      </div>
      {showReview && (
        <UploadReviewModal
          files={pendingFiles}
          existingFilenames={existingNames}
          onCancel={() => { setShowReview(false); setPendingFiles([]); }}
          onConfirm={(files, opts) => startUploads(files, opts)}
        />
      )}
    </div>
  );
};

export default S3FileUpload;




